import debounce from 'just-debounce-it';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { ReactComponent as IconMinus } from '../../assets/icons/icon-minus.svg';
import { ReactComponent as IconPlus } from '../../assets/icons/icon-plus.svg';
import { NotificationMessages } from '../../common/notificationMessages';
import FlashMessages from '../../components/ui/FlashMessages';

interface Props {
  currentQuantity?: number;
  disabled?: boolean;
  onChange: (quantity: number) => void;
  maxQuantity?: number;
  size?: 'small' | 'regular';
  withDebounce?: boolean;
}

const Quantity: React.FC<Props> = ({
  currentQuantity,
  disabled,
  maxQuantity,
  onChange,
  size = 'regular',
  withDebounce = true,
}) => {
  const [canUpdate, setCanUpdate] = useState<boolean>(false);
  const [maxLimit, setMaxLimit] = useState<number>(
    maxQuantity && maxQuantity > 0 ? maxQuantity : 20
  );
  const [quantity, setQuantity] = useState<number>(currentQuantity || 1);

  const handleDecrement = () => {
    if (+quantity > 1) {
      setQuantity(+quantity - 1);
    }
  };

  const handleIncrement = () => {
    if (disabled || +quantity >= maxLimit) {
      FlashMessages.warn(NotificationMessages.MAX_QUANTITY_REACHED);
      return;
    }
    if (+quantity < maxLimit) {
      setQuantity(+quantity + 1);
    }
  };

  useEffect(() => {
    setQuantity(currentQuantity || 1);
  }, [currentQuantity]);

  useEffect(() => {
    if (!canUpdate) {
      return;
    }

    const debouncedOnChange = debounce(() => {
      onChange(+quantity);
    }, 400);

    if (+quantity > maxLimit) {
      setQuantity(maxLimit);
    }

    if (+quantity < 0) {
      setQuantity(1);
    }

    if (withDebounce) {
      debouncedOnChange();
    } else {
      onChange(+quantity);
    }

    return () => {
      withDebounce && debouncedOnChange.cancel();
    };
  }, [quantity]);

  useEffect(() => {
    setCanUpdate(true);
  }, []);

  useEffect(() => {
    if (!maxQuantity || maxQuantity < 0 || maxQuantity > 20) {
      return;
    }
    setMaxLimit(maxQuantity);
    if (+quantity > maxQuantity) {
      setQuantity(maxQuantity);
    }
  }, [maxQuantity]);

  return (
    <div className="product-quantity">
      <div className={classNames('form-group', size)}>
        <div className="form-field icon-before icon-after">
          <span className="icon icon-before">
            <IconMinus
              className="product-quantity-action decrement"
              onClick={() => {
                !disabled && +quantity > 1 && handleDecrement();
              }}
            />
          </span>
          <input
            className={size}
            disabled={disabled}
            max={maxLimit}
            name="quantity"
            type="number"
            value={quantity}
            readOnly
          />
          <span className="icon icon-after">
            <IconPlus
              className="product-quantity-action decrement"
              onClick={() => {
                handleIncrement();
              }}
            />
          </span>
        </div>
      </div>
    </div>
  );
};

export default Quantity;
